import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Waves, CloudLightning, Construction, ZapOff, UserSearch, AlertTriangle, Clock, MapPin, Users, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHubT } from './hubStrings';

export const TYPE_ICONS = {
  wildfire: Flame, flood: Waves, severe_weather: CloudLightning,
  road_closure: Construction, power_outage: ZapOff,
  missing_person: UserSearch, other: AlertTriangle,
};

export const STATUS_STYLES = {
  active: 'bg-red-100 text-red-800 ring-red-300',
  monitoring: 'bg-amber-100 text-amber-800 ring-amber-300',
  resolved: 'bg-emerald-100 text-emerald-800 ring-emerald-300',
  archived: 'bg-gray-100 text-gray-600 ring-gray-300',
};

// Coloured status dot (red active / orange monitoring / green resolved) as CSS
// circles so they stay crisp at any size and match the chip palette.
export const STATUS_DOTS = {
  active: 'bg-red-500 animate-pulse',
  monitoring: 'bg-orange-500',
  resolved: 'bg-emerald-500',
  archived: 'bg-gray-400',
};

export const SEVERITY_STYLES = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  normal: 'bg-slate-200 text-slate-700',
};

export const timeAgo = (iso, lang) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return lang === 'es' ? `hace ${Math.max(mins, 1)} min` : `${Math.max(mins, 1)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return lang === 'es' ? `hace ${hours} h` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return lang === 'es' ? `hace ${days} d` : `${days}d ago`;
};

export const IncidentCard = ({ incident }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const hubT = useHubT();
  const Icon = TYPE_ICONS[incident.incidentType] || AlertTriangle;
  const isCritical = incident.severity === 'critical' && incident.status === 'active';
  const areas = incident.areasAffected || [];

  return (
    <button
      onClick={() => navigate(`/incidents/${incident.id}`)}
      className={`w-full text-left bg-white rounded-xl border p-4 shadow-sm hover:shadow transition-shadow ${
        isCritical ? 'border-red-400 ring-2 ring-red-200' : 'border-gray-200'
      }`}
      data-testid={`incident-card-${incident.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`rounded-lg p-2 flex-shrink-0 ${isCritical ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold ring-1 ${STATUS_STYLES[incident.status] || STATUS_STYLES.archived}`}>
              <span className={`h-2 w-2 rounded-full ${STATUS_DOTS[incident.status] || STATUS_DOTS.archived}`} />
              {hubT(`status_${incident.status}`)}
            </span>
            {incident.severity !== 'normal' && (
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${SEVERITY_STYLES[incident.severity]}`}>
                {hubT(`sev_${incident.severity}`)}
              </span>
            )}
            <span className="text-[11px] text-gray-500">{hubT(`it_${incident.incidentType}`)}</span>
          </div>
          <h3 className="text-base font-semibold text-gray-900 truncate">{incident.title}</h3>
          <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">{incident.description}</p>
          <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {areas.slice(0, 3).join(', ')}{areas.length > 3 ? ` +${areas.length - 3}` : ''}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {hubT('lastOfficialUpdate')}: {timeAgo(incident.lastOfficialUpdateAt || incident.updatedAt, i18n.language)}
            </span>
            {incident.followerCount > 0 && (
              <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{incident.followerCount}</span>
            )}
            {incident.reportCount > 0 && (
              <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{incident.reportCount}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default IncidentCard;
